import translation from 'locales/translation';
import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { PautaEvento } from 'api/Assessor/pautas';

import { Card, Grid, Typography } from '@material-ui/core';
import { AlertIcon, SyncIcon } from 'components/Icons';

import { IReducer } from 'store/Reducer';

import AnexoPautaModal from 'components/Modal/__LEGACY__/AnexoPautaModal/AnexoPautaModal';
import ComissaoEleitoralModal, {
  ComissaoEleitoralProps,
} from 'components/Modal/__LEGACY__/ComissaoEleitoralModal';
import AssembleiaPautaNewAssessor from 'components/__SHARED__/AssembleiaPautasNewAssessor/AssembleiaPautaNewAssessor';
import {
  AssembleiaPautaStatus,
  getNumberByAssembleiaPautaStatus,
} from 'helpers/enums/AssembleiaPautaStatus';
import { AssembleiaTipo } from 'helpers/enums/AssembleiaTipo';
import { getUniqueKey } from 'helpers/utils/KeyUtils';
import { Paginator } from 'plataforma-fundacao-componentes';
import { assessorActionAtualizaPauta } from 'store/modules/assessor/actions';
import { modalsActionsOpen } from 'store/modules/ModalsActions';
import Context from '../Context';
import Actions from './Actions/index';
import { Caption } from './components';
import useStyle from './style';

interface Props {
  idEvento: number;
  needQuorumRegister?: boolean;
  currentPage: number;
  onChangePage: (value: number) => void;
  presential?: boolean;
  tipo?: AssembleiaTipo;
}

const PAUTAS_PER_PAGE = 10;

export default function Pautas({
  idEvento,
  needQuorumRegister,
  onChangePage,
  currentPage,
  presential,
  tipo,
}: Props) {
  const classes = useStyle();
  const dispatch = useDispatch();
  const { pautasIsLoading, pautas } = useSelector(
    (state: IReducer) => state.assessor
  );
  const { usuario } = useSelector((state: IReducer) => state.auth);

  const hasPermissionToSeeAnexos = useMemo(() => {
    return (
      ((usuario?.perfil.id === 'pertencer_consulta' &&
        !usuario?.perfil.assessorRoute.assembleias.pautas.anexos.ver &&
        tipo === 'REU') ||
        usuario?.perfil.assessorRoute.assembleias.pautas.anexos.ver) ??
      false
    );
  }, [
    tipo,
    usuario?.perfil.assessorRoute.assembleias.pautas.anexos.ver,
    usuario?.perfil.id,
  ]);

  const needCaption = (pautas: PautaEvento[]) => {
    return pautas.some((pauta) => pauta.flgRestrita || pauta.flgSecreta);
  };

  const { setPautas } = useContext(Context);

  const [opened, setOpened] = useState<any>();

  const openModalComissaoEleitoral = useCallback(() => {
    const modalKey: string = getUniqueKey();
    dispatch(
      modalsActionsOpen(
        ComissaoEleitoralModal,
        {
          genericProps: {
            key: modalKey,
            idEvento,
            isAssessor: true,
          } as ComissaoEleitoralProps,
        },
        modalKey
      )
    );
  }, [dispatch, idEvento]);

  useEffect(() => {
    setPautas(pautas!?.totalElements);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pautas]);

  const handleOpenModalAnexos = useCallback(
    (idEvento: number, idPauta: number) => {
      const key: string = getUniqueKey();
      dispatch(
        modalsActionsOpen(
          AnexoPautaModal,
          {
            genericProps: {
              key,
              idPauta,
              idEvento,
            },
          },
          key
        )
      );
    },
    [dispatch]
  );

  const handleDelete = useCallback(() => {
    if (!pautas) return;
    const ceil = Math.ceil((pautas.totalElements - 1) / PAUTAS_PER_PAGE - 1);
    const newPage = ceil < currentPage ? ceil : currentPage;
    onChangePage(newPage < 0 ? 0 : newPage);
  }, [pautas, currentPage, onChangePage]);

  if (pautasIsLoading) {
    return (
      <Grid component='section' item xs={12}>
        <header className={classes.header}>
          <Typography component='h2' className={classes.headline}>
            {translation.AssembleiaAssociadoRoute.pautas.title}
          </Typography>
        </header>
        <Card className={classes.statusPautas} variant='outlined'>
          <span className={classes.statusPautasIcon}>
            <SyncIcon animated />
          </span>
          <Typography>
            {translation.AssembleiaAssociadoRoute.pautas.loading}
          </Typography>
        </Card>
      </Grid>
    );
  }

  if (!pautas?.elements.length) {
    return (
      <Grid component='section' item xs={12}>
        <header className={classes.header}>
          <Typography component='h2' className={classes.headline}>
            {translation.AssembleiaAssociadoRoute.pautas.title}
          </Typography>
        </header>
        <Card className={classes.statusPautas} variant='outlined'>
          <span
            className={[
              classes.statusPautasIcon,
              classes.statusPautaIconWarning,
            ].join(' ')}
          >
            <AlertIcon />
          </span>
          <Typography>
            {translation.AssembleiaAssociadoRoute.pautas.notFound}
          </Typography>
        </Card>
      </Grid>
    );
  }

  const toggleOpened = (reference: number, value: boolean) => {
    if (value) {
      setOpened(reference);
    } else if (opened === reference) {
      setOpened(undefined);
    }
  };

  return (
    <Grid component='section' item xs={12}>
      <header className={classes.header}>
        <Typography component='h2' className={classes.headline}>
          {translation.AssembleiaAssociadoRoute.pautas.title}
        </Typography>
      </header>
      {needCaption(pautas.elements) && <Caption />}
      {Array.from(pautas.elements)
        .sort((a, b) => a.ordem - b.ordem)
        .map((pauta, index) => {
          pauta.paginaPresencial = presential;

          return (
            <Fragment key={index}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  margin: '22px 0',
                }}
              >
                <AssembleiaPautaNewAssessor
                  dataTestid={`pauta-${pauta.idPauta}`}
                  hasPermissionToSeeAnexos={hasPermissionToSeeAnexos}
                  status={
                    pauta.paginaPresencial
                      ? getNumberByAssembleiaPautaStatus(pauta.statusPresencial)
                      : pauta.status
                  }
                  tempo={pauta.tempoRestante}
                  titulo={pauta.titulo}
                  descricao={pauta.descricao}
                  itens={pauta!?.itens!?.map((x: any) => x)}
                  opened={
                    (pauta.status !== 1 && opened === pauta.idPauta) ||
                    (pauta.status === 1 && opened === pauta.idPauta)
                  }
                  allwaysOpened={
                    (presential && pauta.statusPresencial === 'LIBERADA') ||
                    (!presential && pauta.status === 1)
                  }
                  toggleOpened={(value) => toggleOpened(pauta.idPauta, value)}
                  flgRestrita={pauta.flgRestrita}
                  flgSecreta={pauta.flgSecreta}
                  tipoPauta={pauta.tipoPauta}
                  agency={pauta.codigoAgencia ? pauta.codigoAgencia : '--'}
                  nucleou={pauta.numeroNucleo ? pauta.numeroNucleo : '--'}
                  callBackZerouTempo={
                    pauta.status === 1
                      ? () => {
                          toggleOpened(pauta.idPauta, false);

                          return dispatch(
                            assessorActionAtualizaPauta(pauta.idPauta, {
                              status: AssembleiaPautaStatus.Encerrada,
                              tempoVotacao: 0,
                              tempoRestante: 0,
                            })
                          );
                        }
                      : () => {}
                  }
                  onClickAnexos={() =>
                    handleOpenModalAnexos(idEvento, pauta.idPauta)
                  }
                  actions={
                    <Actions
                      pauta={pauta}
                      blockedUntilQuorum={needQuorumRegister}
                      currentPage={currentPage}
                      onDelete={handleDelete}
                    />
                  }
                  electionCommission={openModalComissaoEleitoral}
                  presential={presential}
                />
              </div>
            </Fragment>
          );
        })}
      <Paginator
        maxLength={5}
        paginaAtual={currentPage}
        onChange={onChangePage}
        quantidadeDePaginas={pautas.totalPages}
      />
    </Grid>
  );
}
